import { ArraySourceComposer } from '@/auth/api/connector'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { analisi, azienda, gruppo_aziendale, sede } from '@/model/qsadminapi/QsAdminApiModuleModel'
import {
  ButtonItem,
  ButtonOptions,
  Form as DXForm,
  GroupItem,
  RequiredRule,
  SimpleItem,
  Tab,
  TabbedItem,
  TabPanelOptions,
  type FormRef as DXFormRef,
} from 'devextreme-react/form'
import DataSource from 'devextreme/data/data_source'
import { ValueChangedEvent as ValueChangedEventSelectBox } from 'devextreme/ui/select_box'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useRevalidator, useSearchParams } from 'react-router-dom'
import { FieldDataChangedEvent } from 'devextreme/ui/form'
import { ClickEvent } from 'devextreme/ui/button'
import notify from 'devextreme/ui/notify'
import LoadingPanel from '@/components/loading-panel/LoadingPanel'
import {
  customButton,
  isUserRoleAllowed,
  onInitializedHtmlEditor,
  onValueChangedHtmlEditor,
  toolbarHtmlEditor,
} from '@/routes/utils'
import { PopupRef } from 'devextreme-react/cjs/popup'
import FormPopupClientNote from '@/components/form-popup-client-note/FormPopupClientNote'
import { Roles } from '@/auth/azure/Roles'
import { CustomizationEditorFormProps } from '@/routes/customizations/CustomizationEditor.types'
import { useScreenSize } from '@/themes/media-query'
import BriefAnalysisGridMemoed from '@/components/brief-grids/BriefAnalysisGrid'
import { ODataStore } from 'devextreme/common/data'
import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import { useTokenRefresh } from '@/auth/azure/azureManager'
import { StatoAnalisi } from '@/routes/analysis/AnalysisEditor.enums'

const CustomizationEditorForm = (props: CustomizationEditorFormProps) => {
  const { creating, customization, aziende, isRoleAllowed, userInfo, fetcher, impiegati, analisi } = props
  const { clientId } = useParams()
  const { service } = useQsAdminApiManager()

  const [searchParams] = useSearchParams()
  const analysisId = searchParams.get('analysisId')

  const formRef = useRef<DXFormRef>(null)
  const revalidator = useRevalidator()
  const popupNoteClienteRef = useRef<PopupRef>(null)

  const token = useTokenRefresh()
  const currentScreenSize = useScreenSize()

  const [currentCompany, setCurrentCompany] = useState<azienda | null | undefined>(
    clientId ? aziende[0] : customization?.sede?.azienda,
  )
  const [currentCompanyChanged, setCurrentCompanyChanged] = useState<boolean>(clientId && creating ? true : false)
  const [sediDataSource, setSediDataSource] = useState<DataSource>(
    ArraySourceComposer('id', customization?.sede?.azienda?.sedi),
  )
  const [currentSite, setCurrentSite] = useState<sede | null | undefined>()
  const [currentSiteChanged, setCurrentSiteChanged] = useState<boolean>(false)

  const [analisiDataSource, setAnalisiDataSource] = useState<DataSource>(ArraySourceComposer('id', analisi))

  const [currentGruppoAziendale, setCurrentGruppoAziendale] = useState<gruppo_aziendale | null | undefined>(
    customization?.sede?.azienda?.gruppo_aziendale,
  )

  const getCompanySites = useCallback(async () => {
    const sitesData = await service.sede().query((builder, sede) => {
      builder.filter(sede.azienda.props.id.eq(Number(currentCompany?.id)))
      builder.orderBy(sede.principale.desc())
      builder.expanding('azienda', (aziendaSedeBuilder, azienda) => {
        aziendaSedeBuilder.expanding('sedi', (sediAziendaSedeBuilder, sedeAzienda) => {
          sediAziendaSedeBuilder.select('id', 'nome', 'note')
          sediAziendaSedeBuilder.orderBy(sedeAzienda.principale.desc())
        })
        aziendaSedeBuilder.expanding('gruppo_aziendale', (gruppoAziendaleBuilder, gruppo_aziendale) => {
          gruppoAziendaleBuilder.select('id', 'nome')
        })
        aziendaSedeBuilder.select('id', 'nome', 'sedi', 'gruppo_aziendale')
      })
      builder.select('id', 'nome', 'azienda', 'note')
    })
    return sitesData.data.value
  }, [currentCompany?.id, service])

  const getAnalysis = useCallback(async () => {
    console.log('gruppo', currentGruppoAziendale)
    const analisiData = await service.analisi().query((builder, analisi) => {
      builder.filter(
        currentGruppoAziendale
          ? analisi.sede.props.azienda.props.gruppo_aziendale.props.id.eq(Number(currentGruppoAziendale.id))
          : analisi.sede.props.azienda.props.id.eq(Number(currentCompany?.id)),
      )
      builder.expanding('sede', (sedeBuilder) => {
        sedeBuilder.expanding('azienda', (aziendaBuilder, azienda) => {
          aziendaBuilder.select('id', 'nome')
        })
        sedeBuilder.select('id', 'nome', 'azienda')
      })
      builder.select('id', 'ded_Dis', 'oggetto', 'sede')
      builder.orderBy(analisi.ded_Dis.desc())
    })

    return analisiData.data.value
  }, [currentCompany?.id, currentGruppoAziendale, service])

  const getAziendaData = useCallback(async () => {
    const aziendaData = await service.azienda(Number(currentCompany?.id)).query((builder, azienda) => {
      builder.expanding('gruppo_aziendale', (gruppoBuilder, gruppo) => {
        gruppoBuilder.select('id', 'nome')
      })
      builder.select('gruppo_aziendale')
    })
    return aziendaData.data
  }, [currentCompany?.id, service])

  useEffect(() => {
    if (!currentCompanyChanged) return
    console.log('COMPANY EFFECT', currentCompany)
    if (currentCompany) {
      getCompanySites().then((sites: sede[]) => {
        console.log('SITES', sites)
        setSediDataSource(ArraySourceComposer('id', sites))
        formRef.current?.instance().updateData('sede', sites[0])
        setCurrentSiteChanged(true)
      })
      getAziendaData().then((company: azienda) => {
        console.log('data', company)
        setCurrentGruppoAziendale(company.gruppo_aziendale ?? null)
      })
      setCurrentCompanyChanged(false)
    }
  }, [currentCompanyChanged, currentCompany, getCompanySites, getAziendaData])

  useEffect(() => {
    if (!currentSiteChanged) return
    console.log('SITE EFFECT', customization.sede)
    if (customization.sede) {
      if (!analysisId && !clientId)
        getAnalysis().then((analysis: analisi[]) => {
          console.log('analysis', analysis)
          setAnalisiDataSource(ArraySourceComposer('id', analysis))
          formRef.current?.instance().updateData('analisis', null)
        })
    } else {
      setAnalisiDataSource(ArraySourceComposer('id', analisi))
    }
    setCurrentSiteChanged(false)
  }, [analisi, analysisId, clientId, currentSiteChanged, customization.sede, getAnalysis])

  return (
    <>
      <LoadingPanel visible={!(fetcher?.state === 'idle' && revalidator.state === 'idle')} />
      <DXForm
        id={'customization_form'}
        formData={customization}
        labelLocation={'top'}
        showValidationSummary={true}
        onFieldDataChanged={(e: FieldDataChangedEvent) => {
          if (
            formRef.current?.instance().option().isDirty !== null &&
            formRef.current?.instance().option().isDirty !== undefined
          ) {
            console.log('is dirty', formRef.current?.instance().option().isDirty)
            formRef.current
              .instance()
              .getButton('btn-save')
              ?.option('disabled', !formRef.current?.instance().option().isDirty)
            formRef.current
              .instance()
              .getButton('btn-cancel')
              ?.option('disabled', !formRef.current?.instance().option().isDirty)
          }
        }}
        validationGroup="customizationValidation"
        ref={formRef}
        readOnly={!isRoleAllowed}
      >
        <GroupItem colCountByScreen={{ xs: 1, sm: 2, md: 2, lg: 2 }}>
          <GroupItem colCountByScreen={{ xs: 1, sm: 1, md: 1, lg: 2 }}>
            <SimpleItem
              label={{ text: 'Azienda' }}
              editorType="dxSelectBox"
              editorOptions={{
                buttons: [
                  customButton('open', (e: ClickEvent) => {
                    if (currentCompany) window.open(`/clients/${currentCompany.id}`, '_blank')
                    else notify(`Deve essere selezionata un' azienda per poterla aprire.`, 'warning', 3000)
                  }),
                  { name: 'dropDown', location: 'after' },
                ],
                value: currentCompany,
                dataSource: ArraySourceComposer('id', aziende),
                placeholder: "Seleziona l'azienda...",
                displayExpr: 'nome',
                searchEnabled: true,
                dropDownOptions: {
                  showTitle: true,
                  title: "Selezionare l'azienda",
                  hideOnOutsideClick: true,
                },
                readOnly: !!clientId || !isRoleAllowed,
                onValueChanged: (e: ValueChangedEventSelectBox) => {
                  if (e.previousValue?.id === e.value?.id) return
                  console.log('COMPANY EVENT CHANGED', e.previousValue, e.value)
                  setCurrentCompany(e.value)
                  setCurrentCompanyChanged(true)
                },
              }}
            >
              <RequiredRule message="Azienda obbligatoria"></RequiredRule>
            </SimpleItem>
            <SimpleItem
              dataField="sede"
              label={{ text: 'Sede' }}
              editorType="dxSelectBox"
              editorOptions={{
                buttons: [
                  customButton(
                    'info',
                    () => {
                      if (customization.sede) {
                        setCurrentSite(customization.sede)
                        popupNoteClienteRef.current?.instance().show()
                      } else notify('Deve essere selezionata una sede per aprire le note.', 'warning', 3000)
                    },
                    false,
                    'Note cliente',
                  ),
                  {
                    name: 'clear',
                    location: 'after',
                  },
                  { name: 'dropDown', location: 'after' },
                ],
                dataSource: sediDataSource,
                placeholder: 'Seleziona la sede...',
                displayExpr: 'nome',
                searchEnabled: true,
                showDropDownButton: true,
                dropDownButtonTemplate: 'dropDownButton',
                dropDownOptions: {
                  showTitle: true,
                  title: 'Selezionare la sede',
                  hideOnOutsideClick: true,
                },
                readOnly: !isRoleAllowed,
                onValueChanged: (e: ValueChangedEventSelectBox) => {
                  if (e.previousValue?.id === e.value?.id) return
                  console.log('SITE EVENT CHANGED', e.previousValue, e.value)
                  setCurrentSiteChanged(true)
                },
              }}
            >
              <RequiredRule message="Sede obbligatoria"></RequiredRule>
            </SimpleItem>

            <SimpleItem
              colSpan={2}
              dataField="responsabili"
              editorType="dxTagBox"
              label={{ text: 'Responsabili' }}
              editorOptions={{
                dataSource: ArraySourceComposer('id', impiegati),
                displayExpr: 'fullname',
                placeholder: 'Seleziona tecnico...',
                applyValueMode: 'instantly',
                showSelectionControls: true,
                searchEnabled: true,
              }}
            >
              <RequiredRule message="Almeno un responsabile obbligatorio"></RequiredRule>
            </SimpleItem>
            <SimpleItem colSpan={2} dataField={'titolo'} editorType={'dxTextArea'}>
              <RequiredRule message="Titolo obbligatorio" />
            </SimpleItem>
            <SimpleItem
              dataField="analisis"
              colSpan={2}
              editorType="dxTagBox"
              label={{ text: 'Analisi' }}
              editorOptions={{
                dataSource: analisiDataSource,
                displayExpr: (item: analisi) => {
                  return item
                    ? `${item.ded_Dis}${item.oggetto ? ' - ' : ''}${item.oggetto ?? ''}${item.sede?.azienda?.nome ? ' - ' : ''}${item.sede?.azienda?.nome ?? ''}${item.sede?.nome ? ' - ' : ''}${item.sede?.nome ?? ''}`
                    : ``
                },
                placeholder: 'Seleziona analisi...',
                applyValueMode: 'instantly',
                showSelectionControls: true,
                searchEnabled: true,
                readOnly: !isRoleAllowed,
                tagTemplate: (itemData: analisi, itemElement: any) => {
                  const tagContent = `${itemData.ded_Dis}${itemData.oggetto ? ' - ' : ''}${itemData.oggetto ?? ''}`

                  itemElement.innerHTML = `
                        <div class="dx-tag-content" style="cursor: pointer; display: flex; align-items: center;">
                          <span>${tagContent}</span>
                          <div class="dx-tag-remove-button" style="margin-left: 4px;"></div>
                        </div>
                      `
                  // Add click handler to the span only (not the remove button)
                  const tagTextElement = itemElement.querySelector('.dx-tag-content span')
                  tagTextElement?.addEventListener('click', (e: MouseEvent) => {
                    e.stopPropagation()
                    console.log('Tag clicked:', itemData)
                    // Navigate to customization or open in new tab
                    window.open(`/analysis/${itemData.id}`, '_blank')
                  })

                  // Add hover effect
                  tagTextElement?.addEventListener('mouseenter', (e: MouseEvent) => {
                    ;(e.target as HTMLElement).style.fontWeight = 'bold'
                  })

                  tagTextElement?.addEventListener('mouseleave', (e: MouseEvent) => {
                    ;(e.target as HTMLElement).style.fontWeight = 'normal'
                  })
                },
              }}
            ></SimpleItem>
            <SimpleItem dataField="supporto_sviluppo" label={{ text: 'Notifica' }} editorType="dxSwitch" />
          </GroupItem>
          <GroupItem colCount={1}>
            <GroupItem colSpan={3}>
              <SimpleItem
                dataField="note"
                label={{ visible: true, text: 'Note' }}
                editorType="dxHtmlEditor"
                editorOptions={{
                  stylingMode: 'filled',
                  valueType: 'html',
                  toolbar: toolbarHtmlEditor(currentScreenSize),
                  mediaResizing: {
                    enabled: true,
                  },
                  imageUpload: {
                    tabs: ['file', 'url'],
                    fileUploadMode: 'base64',
                  },
                  height: 330,
                  onValueChanged: onValueChangedHtmlEditor,
                  onInitialized: onInitializedHtmlEditor,
                }}
              ></SimpleItem>
            </GroupItem>
          </GroupItem>
        </GroupItem>
        <GroupItem
          visible={isUserRoleAllowed(userInfo?.roles, [
            Roles.GlobalAdministrator,
            Roles.Administrator,
            Roles.TechDeveloper,
          ])}
          cssClass="last-group"
        >
          <GroupItem cssClass="buttons-group" colCount={creating ? 1 : 2}>
            <ButtonItem name="btn-save">
              <ButtonOptions
                disabled={!formRef.current?.instance().option().isDirty}
                icon="save"
                text="Salva"
                width="150px"
                onClick={async () => {
                  const validationResult = formRef.current?.instance().validate()
                  if (!validationResult?.isValid) return
                  try {
                    if (customization) {
                      fetcher?.submit(JSON.stringify(customization), {
                        method: creating ? 'POST' : 'PUT',
                        encType: 'application/json',
                      })
                    } else console.error('no data tu submit')
                  } catch (err) {
                    console.log('error', err)
                  }
                }}
              />
            </ButtonItem>
            {!creating && (
              <ButtonItem name="btn-cancel">
                <ButtonOptions
                  disabled={!formRef.current?.instance().option().isDirty}
                  width="150px"
                  text="Annulla"
                  icon="undo"
                  onClick={() => {
                    revalidator.revalidate()
                  }}
                />
              </ButtonItem>
            )}
          </GroupItem>
        </GroupItem>
      </DXForm>
      <FormPopupClientNote
        data={currentSite}
        refPopup={popupNoteClienteRef}
        refreshDataSourceSites={() => {
          if (currentCompany) {
            getCompanySites().then((sites: sede[]) => {
              console.log('SITES', sites)
              setSediDataSource(ArraySourceComposer('id', sites))
              formRef.current?.instance().updateData(
                'sede',
                sites.find((site: sede) => {
                  return site.id === currentSite?.id
                }),
              )
            })
          }
        }}
        isRoleAllowed={isUserRoleAllowed(userInfo?.roles, [
          Roles.TechDeveloper,
          Roles.Sales,
          Roles.GlobalAdministrator,
          Roles.ExternalSales,
          Roles.Administrator,
        ])}
      ></FormPopupClientNote>
    </>
  )
}

export default CustomizationEditorForm
